import React, { useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";

import { useGetFieldShape, useTranslation } from "utils";

import {
  Card,
  Checkbox,
  Divider,
  FieldMatchCardProps,
  Flex,
  Icon,
  MapBoundaryCompare,
  Modal,
  STATUS_COLOR_MAP,
  STATUS_LABEL_MAP,
  TagNormal,
  Tooltip,
  Typography,
  buildRows,
  deriveMatchStatus,
  getDocFieldBoundaries,
  parseLocation,
} from "components";

// Matches the admin polygon-overlay comparison colors (PR #1277) so the platform/document
// color meaning stays consistent between the admin and farmer-facing views.
const PLATFORM_COLOR = "#1A73E8";
const DOCUMENT_COLOR = "#FF8C00";

export const PLFieldMatchCard = ({
  record,
  isSelected,
  hideStatuses,
  onToggle,
  onChangeField,
  rightContent,
  headerActions,
  className,
  dataTestId,
}: FieldMatchCardProps) => {
  const { t } = useTranslation("docScans");
  const { ref, inView } = useInView({ triggerOnce: true });
  const [showMapModal, setShowMapModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const fieldId = record.fieldId ?? "";
  const matchStatus = deriveMatchStatus(record);
  const rows = buildRows(record.farmFieldRaw, record.docFieldRaw, record.statuses).filter((row) => row.label !== "cropCode");
  const fieldLocation = record.farmFieldRaw?.location ? parseLocation(record.farmFieldRaw.location) : undefined;

  const fieldName = record.farmFieldRaw?.name ?? "-";
  const cropName = record.docFieldRaw?.crops?.[0]?.crop_name ?? "-";
  const fieldSize = record.farmFieldRaw?.size;

  const { fieldShape } = useGetFieldShape({ fieldId, enabled: inView });

  const parcelBoundaries = useMemo(() => getDocFieldBoundaries(record.docFieldRaw), [record.docFieldRaw]);
  const hasDocumentGeometry = parcelBoundaries.length > 0;

  const resolvedTooltip = matchStatus === "auto_matched" ? t("fieldMatchCard.statusTooltip.autoMatched") : undefined;

  const legend = (
    <Flex gap="1rem" className="pl-field-match-card-legend" data-testid="pl-field-match-card-legend">
      <Flex align="center" gap="0.5rem">
        <span className="pl-field-match-card-legend-dot pl-field-match-card-legend-dot--platform" />
        <Typography variant="paragraph-s">{t("fieldMatchCard.legend.platform")}</Typography>
      </Flex>
      {hasDocumentGeometry && (
        <Flex align="center" gap="0.5rem">
          <span className="pl-field-match-card-legend-dot pl-field-match-card-legend-dot--document" />
          <Typography variant="paragraph-s">{t("fieldMatchCard.legend.document")}</Typography>
        </Flex>
      )}
    </Flex>
  );

  return (
    <Card customRef={ref} className={`pl-field-match-card ${className ?? ""}`} border dataTestId={dataTestId}>
      {fieldLocation && (
        <div className="pl-field-match-card-hero" data-testid="pl-field-match-card-hero">
          <MapBoundaryCompare
            primaryGeometry={fieldShape?.geometry ?? null}
            secondaryGeometries={parcelBoundaries}
            location={fieldLocation}
            primaryColor={PLATFORM_COLOR}
            secondaryColor={DOCUMENT_COLOR}
            boundsPadding={16}
            primaryWeight={1}
            dashWeight={2}
            dashScale={3}
          />
          <button
            type="button"
            className="pl-field-match-card-expand"
            onClick={() => setShowMapModal(true)}
            aria-label={t("fieldMatchCard.expandMap")}
            data-testid="pl-field-match-card-expand"
          >
            <Icon iconName="tabler/arrows-maximize" size="s" color="#fff" />
          </button>
          <div className="pl-field-match-card-hero-legend">{legend}</div>
        </div>
      )}

      <Flex align="center" gap="1rem" className="field-match-card-header padding-100 padding-right-50">
        <Flex vertical flex={1} className={!isSelected ? "faded" : ""} data-testid="field-match-card-field-info">
          <Typography variant="paragraph-l" data-testid="field-match-card-field-name">
            {fieldName}
          </Typography>
          <Typography variant="paragraph-s" color="grey-600" data-testid="field-match-card-field-meta">
            {t("fieldMatchCard.fieldMetaCrop", { crop: cropName, size: fieldSize })}
          </Typography>
        </Flex>

        <Flex align="center" gap="1rem">
          {headerActions ?? (
            <>
              {onChangeField && (
                <Typography
                  variant="paragraph-m"
                  color="grey-600"
                  bold
                  className="change-field-link pointer"
                  onClick={onChangeField}
                  data-testid="field-match-card-change-field"
                >
                  {t("fieldMatchCard.changeField")}
                </Typography>
              )}

              <Tooltip title={resolvedTooltip} disabled={!resolvedTooltip}>
                <TagNormal
                  color={STATUS_COLOR_MAP[matchStatus]}
                  className="margin-0"
                  text={t(`fieldMatchCard.${STATUS_LABEL_MAP[matchStatus]}`)}
                  dataTestId="field-match-card-status-tag"
                />
              </Tooltip>

              <Tooltip title={t(`fieldMatchCard.checkboxTooltip.${isSelected ? "remove" : "confirm"}`)}>
                <Checkbox checked={isSelected} onChange={() => onToggle(record.id)} />
              </Tooltip>
            </>
          )}
        </Flex>
      </Flex>

      <Divider className="field-match-card-divider" />

      {rightContent ? (
        <div className="field-match-card-table-right pl-field-match-card-picker" data-testid="field-match-card-right-content">
          {rightContent}
        </div>
      ) : (
        <>
          <button
            type="button"
            className="pl-field-match-card-details-toggle"
            onClick={() => setIsExpanded((prev) => !prev)}
            data-testid="pl-field-match-card-details-toggle"
          >
            <Icon iconName={isExpanded ? "tabler/chevron-up" : "tabler/chevron-down"} size="s" />
            <Typography variant="paragraph-s" color="grey-600" bold>
              {t(isExpanded ? "fieldMatchCard.hideFieldDetails" : "fieldMatchCard.showFieldDetails")}
            </Typography>
          </button>

          {isExpanded && (
            <div className="field-match-card-table" data-testid="field-match-card-table">
              <div className="field-match-card-table-left">
                <div className="field-match-card-table-header">
                  <div className="field-match-card-table-cell label-col" />
                  <div className="field-match-card-table-cell klim-col" title={t("fieldMatchCard.columns.klimPlatform")}>
                    {t("fieldMatchCard.columns.klimPlatform")}
                  </div>
                  <div className="field-match-card-table-cell doc-col" title={t("fieldMatchCard.columns.document")}>
                    {t("fieldMatchCard.columns.document")}
                  </div>
                  {!hideStatuses && <div className="field-match-card-table-cell status-col" />}
                </div>

                {rows.map((row) => (
                  <div key={row.label} className="field-match-card-table-row" data-testid={`field-match-card-row-${row.label}`}>
                    <div className="field-match-card-table-cell label-col" title={t(`fieldMatchCard.rows.${row.label}`)}>
                      {t(`fieldMatchCard.rows.${row.label}`)}
                    </div>
                    <div className={`field-match-card-table-cell klim-col${!row.isMatching && !hideStatuses ? " bold" : ""}`} title={row.klimValue}>
                      {row.klimValue}
                    </div>
                    <div className={`field-match-card-table-cell doc-col${!row.isMatching && !hideStatuses ? " bold" : ""}`} title={row.documentValue}>
                      {row.documentValue}
                    </div>
                    {!hideStatuses && (
                      <div className="field-match-card-table-cell status-col" data-testid={`field-match-card-status-${row.label}`}>
                        {row.isMatching ? <Icon iconName="tabler/check" size="s" className="match-icon" color="#065c45" /> : <div className="mismatch-dot" />}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <Modal
        dataTestId="pl-field-match-card-map-modal"
        showModal={showMapModal}
        onCancel={() => setShowMapModal(false)}
        maskClosable
        footer={null}
        title={t("fieldMatchCard.boundaryComparisonTitle")}
        content={
          <Flex vertical gap="0.75rem">
            <div className="pl-field-match-card-modal-map">
              <MapBoundaryCompare
                primaryGeometry={fieldShape?.geometry ?? null}
                secondaryGeometries={parcelBoundaries}
                location={fieldLocation}
                primaryColor={PLATFORM_COLOR}
                secondaryColor={DOCUMENT_COLOR}
                zoomable
                pannable
                boundsPadding={24}
              />
            </div>
            {legend}
          </Flex>
        }
      />
    </Card>
  );
};
